import {
  AddressDto,
  CreateAddressForm,
  createAddressSchema,
  UpdateAddressForm,
  updateAddressSchema,
} from '@act/shared/data-transfer-objects';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  CircularProgress,
} from '@mui/material';
import { getDialogContainer } from './utils/dialog-container';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { GenericDialogProps } from 'utilities/dialog-context/dialog-context-helpers';
import {
  AddressContent,
  EditAddressContent,
} from './components/AddressContent';
import {
  DialogContentContainer,
  DialogTitleBarComponent,
} from './components/SharedDialogHookComponents';
import useConfirmationAlertDialog from './hooks/use-confirmation-alert-dialog';
import useUnsavedChangesDialog from './hooks/use-unsaved-changes-dialog';
import { Button } from '@act/storybook';
import { zodResolver } from '@hookform/resolvers/zod';
import { useApiCall } from 'redux/utils/api-hook';
import { userRequests } from 'redux/requests/users.request';
import { useToast } from '../app/ReusableToast';
import { useAppDispatch } from 'redux/store-hooks';
import {
  createAddressSuccess,
  getMember,
  updatedAddressSuccess,
  updatedPrimaryAddressSuccess,
} from 'redux/slices/memberSlice';

export interface AddressUniqueProps {
  mode: 'Add' | 'Delete' | 'Edit' | 'SetPrimary';
  memberId: string;
  addresses: AddressDto[];
}

export type AddressDialogProps = GenericDialogProps<boolean> &
  AddressUniqueProps;

export type AddressDialogCallbackInput = {};

export const AddressDialog = ({
  mode,
  memberId,
  addresses,
  open,
  setOpen,
  callback,
}: AddressDialogProps) => {
  const openConfirmationAlertDialog = useConfirmationAlertDialog();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const toast = useToast();
  const dispatch = useAppDispatch();
  const [selectedAddress, setSelectedAddress] = useState<AddressDto>(null);

  const [createAddressResult, isCreating, createAddress] = useApiCall(
    userRequests.createAddress
  );

  const [updateAddressResult, isUpdating, updateAddress] = useApiCall(
    userRequests.updateAddress
  );

  const [deleteAddressResult, isDeleting, deleteAddress, deleteAddressError] =
    useApiCall(userRequests.deleteAddress);

  const [updatePrimaryAddressResult, _isUpdatingPrimary, updatePrimaryAddress] =
    useApiCall(userRequests.updatePrimaryAddress);

  const isLoading = isCreating || isUpdating || isDeleting;

  const activeAddresses = addresses?.filter(a => !a.deletedAt) || [];

  const handleClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        setOpen(false);
        callback(true);
      });
    } else {
      setOpen(false);
      callback(true);
    }
  };
  const schema = mode === 'Edit' ? updateAddressSchema : createAddressSchema;
  const { control, handleSubmit, reset, watch, formState } = useForm<
    CreateAddressForm | UpdateAddressForm
  >({
    resolver: zodResolver(schema),
    defaultValues: selectedAddress || {
      userId: memberId,
      addressId: null,
      description: '',
      line1: '',
      line2: '',
      city: '',
      state: '',
      zip: '',
      primary: false,
    },
  });
  const { errors, isDirty } = formState;

  const selectedAddressId = watch('addressId');

  useEffect(() => {
    if (memberId && mode === 'Add') {
      reset({
        userId: memberId,
        addressId: null,
        description: '',
        line1: '',
        line2: '',
        city: '',
        state: '',
        zip: '',
        primary: false,
      });
    }
  }, [memberId]);

  useEffect(() => {
    if (selectedAddressId) {
      setSelectedAddress(activeAddresses.find(a => a.id === +selectedAddressId));
    }
  }, [selectedAddressId]);

  useEffect(() => {
    if (memberId && selectedAddress) {
      reset({
        userId: memberId,
        addressId: selectedAddress?.id,
        description: selectedAddress?.description || '',
        line1: selectedAddress?.line1,
        line2: selectedAddress?.line2 || '',
        city: selectedAddress?.city,
        state: selectedAddress?.state,
        zip: selectedAddress?.zip,
        primary: selectedAddress?.primary,
      });
    }
  }, [memberId, selectedAddress]);

  useEffect(() => {
    if (
      createAddressResult ||
      updateAddressResult ||
      updatePrimaryAddressResult
    ) {
      if (createAddressResult) {
        dispatch(
          createAddressSuccess({
            userId: memberId,
            address: createAddressResult,
          })
        );
        toast('Address created successfully');
      }
      if (updateAddressResult) {
        dispatch(
          updatedAddressSuccess({
            userId: memberId,
            addressId: selectedAddressId,
            address: updateAddressResult,
          })
        );

        toast('Address updated successfully');
      }
      if (updatePrimaryAddressResult) {
        dispatch(
          updatedPrimaryAddressSuccess({
            userId: memberId,
            address: updatePrimaryAddressResult,
          })
        );
        toast('Address set as primary successfully');
      }
      setOpen(false);
      callback(true);
    }
  }, [createAddressResult, updateAddressResult, updatePrimaryAddressResult]);

  useEffect(() => {
    if (deleteAddressResult && !isDeleting) {
      dispatch(getMember({ params: { id: memberId } }));
      setOpen(false);
      callback(true);
      toast('Address deleted successfully');
    }
  }, [deleteAddressResult, deleteAddressError, isDeleting]);

  async function onSubmit(data: CreateAddressForm | UpdateAddressForm) {
    const {
      addressId,
      city,
      description,
      line1,
      line2,
      primary,
      state,
      userId,
      zip,
    } = data;
    if (mode === 'Add') {
      createAddress({
        params: { userId },
        body: {
          ...(description && { description }),
          line1,
          ...(line2 && { line2 }),
          city,
          state,
          zip,
          primary: primary,
        },
      });
    }
    if (mode === 'Delete') {
      const confirmDelete = await openConfirmationAlertDialog({
        title: 'Confirm delete',
        icon: <ErrorOutlineIcon />,
        iconColor: 'error',
        iconBorderColor: 'errorContainer',
        avatarCaption: 'Are you sure?',
        message:
          'This address will no longer be available to use for this member.',
        confirmationText: 'Yes, delete now',
        confirmButtonColor: 'error',
      });

      if (!confirmDelete) return;

      deleteAddress({
        params: { userId, addressId },
      });
    }
    if (mode === 'Edit') {
      updateAddress({
        params: { userId, addressId },
        body: {
          ...(description && { description }),
          primary,
          line1,
          ...(line2 && { line2 }),
          city,
          state,
          zip,
        },
      });
    }
    if (mode === 'SetPrimary') {
      updatePrimaryAddress({
        params: { userId, addressId },
      });
    }
  }

  const container = getDialogContainer();

  return (
    <div style={{ width: '600px' }}>
      <Dialog open={open} onClose={handleClose} fullWidth container={container}>
        <DialogContentContainer>
          <DialogTitleBarComponent
            title={`${mode === 'SetPrimary' ? 'Set primary' : mode} address`}
            handleClose={handleClose}
          />
          <DialogContent>
            {mode === 'Add' && (
              <AddressContent control={control} errors={errors} />
            )}
            {(mode === 'Delete' ||
              mode === 'Edit' ||
              mode === 'SetPrimary') && (
              <EditAddressContent
                control={control}
                errors={errors}
                addresses={activeAddresses}
                selectedAddress={selectedAddress}
                isEdit={mode === 'Edit'}
                showDetails={['Delete', 'SetPrimary'].includes(mode)}
              />
            )}
          </DialogContent>
          <Divider />
          <DialogActions sx={{ padding: '8px 24px' }}>
            <Button onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              onClick={handleSubmit(onSubmit)}
              color={mode === 'Delete' ? 'error' : 'primary'}
              debounced
              sx={{ padding: '10px 24px' }}
              disabled={isLoading}
              startIcon={
                isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
            >
              {isLoading ? 'Saving...' : mode === 'Delete' ? 'Delete' : 'Save'}
            </Button>
          </DialogActions>
        </DialogContentContainer>
      </Dialog>
    </div>
  );
};
